import React from 'react';
import { motion } from 'framer-motion';
import { FiMapPin, FiMail, FiPhone, FiUsers, FiAward, FiGlobe } from 'react-icons/fi';
import CTASection from '../components/CTASection';
import SEO from '../components/SEO';
import '../css/about.css';

const About = () => {
  const team = [
    {
      name: 'David Green',
      role: 'Co-Owner',
      image: '/DavidGreen.jpeg',
      bio: 'David Green is a seasoned videographer, director, and producer with over 30 years of experience in broadcast media, live sports, and event production. David produced high-quality video content for corporate clients, staged events, and international broadcasts. His career includes work with the Caribbean Broadcasting Corporation, and has been contracted on projects for major networks such as SkyNews, ESPN Star Sports, and IMG Worldwide, with a strong focus on cricket coverage. A founding member and former President of the Barbados Film & Video Association, David is recognized for his technical expertise, creative storytelling, and leadership in Caribbean media and international sports broadcasting.'
    },
    {
      name: 'Rivelino Simmons',
      role: 'Co-Owner',
      image: '/Riv.jpeg',
      bio: 'Rivelino Simmons is a media consultant with over thirty five years of experience in film, television and digital production. He has worked with major international broadcasters including ESPN, IMG, ITV, CNN, Flow Sports and SportsMax. He produces Caribbean Week in Review, a weekly regional news show distributed in nineteen countries across the Caribbean, the United States, Canada and the United Kingdom. Rivelino specializes in distribution, licensing, rights management and production, building strong partnerships that expand the reach of Caribbean content globally.'
    }
  ];

  const equipment = [
    'Sony FX9 Cinema Cameras',
    'DJI Inspire 2 Drones',
    'Blackmagic ATEM Switchers',
    'Canon C300 Mark III',
    'Sennheiser Wireless Audio',
    'ARRI Lighting Kits',
    'Gimbal Stabilizers',
    'Live Streaming Encoders'
  ];

  const achievements = [
    { number: '50+', label: 'Live Events Covered' },
    { number: '15+', label: 'Countries Reached' },
    { number: '8+', label: 'Platforms' },
    { number: '3', label: 'Languages' }
  ];

  const aboutStructuredData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About Digital Shark Inc",
    "description": "Learn about Digital Shark Inc., a Caribbean Production & Broadcast Studio specializing in TV, Sports, Events & Pay-Per-View content across the Caribbean region.",
    "mainEntity": {
      "@type": "Organization",
      "name": "Digital Shark Inc.",
      "url": "https://digitalshark.tv",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "French Village",
        "addressLocality": "St. Peter",
        "addressCountry": "Barbados"
      },
      "founder": [
        {
          "@type": "Person",
          "name": "David Green",
          "jobTitle": "Co-Owner"
        },
        {
          "@type": "Person", 
          "name": "Rivelino Simmons",
          "jobTitle": "Co-Owner"
        }
      ]
    }
  };

  return (
    <div className="min-h-screen pt-20">
      <SEO 
        title="About Digital Shark Inc - Caribbean Production & Broadcast Studio"
        description="Learn about Digital Shark Inc., a Caribbean Production & Broadcast Studio specializing in TV, Sports, Events & Pay-Per-View content across the Caribbean region."
        keywords="about Digital Shark Inc, Caribbean production studio, broadcast studio, Barbados video production, David Green, Rivelino Simmons, television production team, broadcast services Barbados, live streaming company Caribbean, Pay Per View, PPV systems"
        url="/about"
        structuredData={aboutStructuredData}
      />
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary-900 to-primary-800 text-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              About / Who We Are
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Based in Barbados with reach across the Caribbean, Digital Shark Inc. blends regional insight with international standards.
            </p>
            <p className="text-lg text-gray-300">
              From concept development and filming through post production, broadcast operations, and monetization (PPV, streaming, licensing), we are your full-service media partner.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-primary-800 mb-6">
                Our Mission
              </h2>
              <p className="text-lg text-primary-600 mb-6 leading-relaxed">
                To elevate Caribbean storytelling by delivering world-class production, broadcast, and digital experiences across sports, entertainment, and events.
              </p>
              
              <h2 className="text-3xl md:text-4xl font-bold text-primary-800 mb-6">
                Our Vision
              </h2>
              <p className="text-lg text-primary-600 mb-6 leading-relaxed">
                A connected Caribbean media landscape where local stories reach global audiences, and every event—regardless of size—is produced with excellence.
              </p>
              
              <h2 className="text-3xl md:text-4xl font-bold text-primary-800 mb-6">
                What We Do
              </h2>
              <div className="space-y-4">
                <p className="text-lg text-primary-600 leading-relaxed">
                  <strong>Full Production Services</strong> – Pre-production, filming, crew, lighting, audio, editing, visual effects.
                </p>
                <p className="text-lg text-primary-600 leading-relaxed">
                  <strong>Broadcast & Live Streaming</strong> – Multi-camera live direction, encoding, real-time monitoring.
                </p>
                <p className="text-lg text-primary-600 leading-relaxed">
                  <strong>Pay-Per-View & Monetization</strong> – End-to-end PPV infrastructure: subscriber access, payment gateways, streaming DRM, audience analytics.
                </p>
                <p className="text-lg text-primary-600 leading-relaxed">
                  <strong>Distribution</strong> – Linear TV, regional platforms, syndication, licensing.
                </p>
                <p className="text-lg text-primary-600 leading-relaxed">
                  <strong>Regional Reach</strong> – Though headquartered in Barbados, we operate across the Caribbean, delivering content in Trinidad & Tobago, Jamaica, Guyana, St. Lucia, etc.
                </p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <img
                src="/Cricket/4baf1da0-8b55-4ca5-ab88-fb689735cf16.JPG"
                alt="DigitalShark Team"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary-900/50 to-transparent rounded-2xl"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary-800 mb-6">
              Meet Our Team
            </h2>
            <p className="text-xl text-primary-600 max-w-3xl mx-auto">
              The passionate professionals behind every DigitalShark production
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
              >
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-64 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-primary-800 mb-2">
                    {member.name}
                  </h3>
                  <p className="text-accent-600 font-semibold mb-3">
                    {member.role}
                  </p>
                  <p className="text-primary-600 text-sm leading-relaxed">
                    {member.bio}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Studio Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-primary-800 mb-6">
                Our Studio
              </h2>
              <p className="text-lg text-primary-600 mb-8 leading-relaxed">
                Located in French Village, St. Peter, our state-of-the-art studio facility is equipped with the latest technology to handle productions of any scale.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <FiMapPin className="text-accent-500" size={20} />
                  <div>
                    <h3 className="font-semibold text-primary-800">Location</h3>
                    <p className="text-primary-600">French Village, St. Peter, Barbados</p>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="bg-gray-50 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-primary-800 mb-6">Equipment & Technology</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {equipment.map((item, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-accent-500 rounded-full flex-shrink-0"></div>
                      <span className="text-primary-700 text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Digital Shark Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary-800 mb-6">
              Why Work With Us
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                title: 'Deep regional knowledge + technical expertise',
                description: 'Combining local Caribbean insight with international production standards'
              },
              {
                title: 'End-to-end solutions (you don\'t have to juggle vendors)',
                description: 'Complete solutions from concept to broadcast, handling every aspect of production'
              },
              {
                title: 'Scalable for small shows to stadium events',
                description: 'Flexible infrastructure that adapts to events of any size and scope'
              },
              {
                title: 'Robust technical infrastructure & redundancy',
                description: 'Advanced streaming technology including Pay Per View systems for global reach'
              },
              {
                title: 'Creative, agile, and client-centric approach',
                description: 'Established relationships with broadcasters, federations, and cultural institutions'
              },
              {
                title: 'Global Distribution Network',
                description: 'Proven ability to deliver content to audiences worldwide across multiple platforms'
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <h3 className="text-xl font-bold text-primary-800 mb-3">
                  {item.title}
                </h3>
                <p className="text-primary-600 leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="section-padding bg-primary-800 text-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Our Impact
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Numbers that tell the story of our growth and reach across the Caribbean
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {achievements.map((achievement, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-accent-400 mb-2">
                  {achievement.number}
                </div>
                <div className="text-gray-300 font-medium">
                  {achievement.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection />
    </div>
  );
};

export default About;
